(() => {
  if (customElements.get('queen-community-cards')) return;

  class QueenCommunityCards extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.endpoint = (this.dataset.endpoint || '').replace(/\/$/, '');
      this.loggedIn = this.dataset.loggedIn === 'true';
      this.status = this.querySelector('[data-queen-status]');
      this.cards = [...this.querySelectorAll('[data-queen-card]')];

      if (this.loggedIn) {
        this.addEventListener('click', this.handleClick.bind(this));
      }

      this.hydrate();
    }

    async hydrate() {
      if (!this.endpoint || !this.cards.length) return;
      const ids = this.cards.map((card) => card.dataset.cardId).filter(Boolean);
      if (!ids.length) return;

      try {
        const payload = await this.request(`${this.endpoint}/cards?ids=${encodeURIComponent(ids.join(','))}`);
        const records = payload.cards || (payload.data && payload.data.cards) || [];

        this.cards.forEach((card) => {
          const record = Array.isArray(records)
            ? records.find((item) => String(item.id || item.cardId) === card.dataset.cardId)
            : records[card.dataset.cardId];
          if (record) this.applyCardState(card, record);
        });
        this.dataset.sync = 'ready';
      } catch (_error) {
        this.dataset.sync = 'offline';
      }
    }

    async handleClick(event) {
      const button = event.target.closest('button[data-reaction]');
      if (!button || !this.contains(button) || button.disabled) return;

      const card = button.closest('[data-queen-card]');
      const reaction = button.dataset.reaction;
      const active = button.getAttribute('aria-pressed') !== 'true';
      if (!card || !reaction) return;

      if (!this.endpoint) {
        this.announce('Community reactions are not connected yet.');
        return;
      }

      this.setBusy(button, true);
      try {
        const payload = await this.request(
          `${this.endpoint}/cards/${encodeURIComponent(card.dataset.cardId)}/reaction`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ reaction, active })
          }
        );
        const record = payload.card || payload.data || payload;
        this.applyCardState(card, record);
        const actionLabel = reaction === 'like' ? 'like' : 'save';
        this.announce(active
          ? `${this.cardTitle(card)} ${actionLabel} added.`
          : `${this.cardTitle(card)} ${actionLabel} removed.`);
      } catch (error) {
        if (error.status === 401 && this.dataset.loginUrl) {
          window.location.assign(this.dataset.loginUrl);
          return;
        }
        this.announce("We couldn't update that reaction. Please try again.");
      } finally {
        this.setBusy(button, false);
      }
    }

    applyCardState(card, record) {
      this.applyReaction(card, 'like', record.likeCount, record.liked);
      this.applyReaction(card, 'save', record.saveCount, record.saved);
    }

    applyReaction(card, reaction, countValue, activeValue) {
      const control = card.querySelector(`[data-reaction="${reaction}"]`);
      if (!control) return;

      const fallbackCount = Number(control.dataset.count || 0);
      const count = Number.isFinite(Number(countValue))
        ? Math.max(0, Number(countValue))
        : fallbackCount;
      const active = typeof activeValue === 'boolean'
        ? activeValue
        : control.getAttribute('aria-pressed') === 'true';
      const countNode = control.querySelector('[data-reaction-count]');
      const labelNode = control.querySelector('.queen-reaction__label');

      control.dataset.count = String(count);
      if (countNode) countNode.textContent = this.formatCount(count);
      if (control.tagName === 'BUTTON') control.setAttribute('aria-pressed', String(active));
      control.classList.toggle('is-active', active);
      if (control.tagName === 'BUTTON' && labelNode) {
        labelNode.textContent = reaction === 'like'
          ? (active ? 'Liked' : 'Like')
          : (active ? 'Saved' : 'Save');
      }

      const title = this.cardTitle(card);
      const noun = reaction === 'like' ? 'likes' : 'saves';
      const verb = reaction === 'like' ? 'like' : 'save';
      if (control.tagName === 'BUTTON') {
        control.setAttribute('aria-label', `${active ? 'Remove' : verb} ${title}. ${count} ${noun}.`);
      } else {
        control.setAttribute('aria-label', `Log in to ${verb} ${title}. ${count} ${noun}.`);
      }
    }

    setBusy(button, busy) {
      button.disabled = busy;
      button.classList.toggle('is-loading', busy);
      button.setAttribute('aria-busy', String(busy));
    }

    cardTitle(card) {
      return (card.querySelector('.queen-card__title')?.textContent || 'Queen').trim();
    }

    formatCount(count) {
      return new Intl.NumberFormat('en-US', {
        notation: count >= 1000 ? 'compact' : 'standard',
        maximumFractionDigits: 1
      }).format(count);
    }

    announce(message) {
      if (!this.status) return;
      this.status.textContent = '';
      window.requestAnimationFrame(() => { this.status.textContent = message; });
    }

    async request(url, options = {}) {
      const response = await fetch(url, {
        credentials: 'same-origin',
        ...options,
        headers: { Accept: 'application/json', ...(options.headers || {}) }
      });
      if (!response.ok) {
        const error = new Error(`Request failed with ${response.status}`);
        error.status = response.status;
        throw error;
      }
      return response.json();
    }
  }

  customElements.define('queen-community-cards', QueenCommunityCards);
})();
